// Grunddaten: Einheiten, Steuerschlüssel mit datierten Sätzen, Warengruppen, Zahlungsbedingungen, Gründe.
// Kleine Tabellen mit derselben Form – eine Fabrik baut je Gegenstand die sechs Verträge
//   list · get · create · update · deactivate · reactivate
// Lesen darf jede angemeldete Person (jede Auswahlliste braucht diese Daten), ändern nur mit dem jeweiligen Recht.
import { PERMISSION_AUTHENTICATED, defineContract, type PermissionKey } from '@shared/contract'
import { VATEX_CODES } from '@shared/einvoice'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { REASON_KINDS, TAX_CATEGORIES, UNIT_KINDS } from '../lib/labels'
import { zBasisPoints, zById, zDay, zExpectedVersion, zOptionalId, zOptionalText, zSaved, zText, zToggle } from './common'

interface ReferenceSpec<E extends string, Row extends z.ZodType, C extends z.ZodRawShape, U extends z.ZodRawShape, F extends z.ZodRawShape, K extends string> {
  /** zweites Segment der Aktionsnamen: 'unit' → masterdata.unit.list */
  entity: E
  /** deutsch: Einzahl, Mehrzahl, Akkusativ mit Artikel („eine Einheit“) */
  one: string
  many: string
  accusative: string
  write: PermissionKey
  row: Row
  create: C
  update: U
  filters?: F
  sortBy: readonly [K, ...K[]]
  keywords: readonly string[]
  /** Sätze für das Sprachmodell: wozu die Daten dienen, was beim Anlegen und Stilllegen gilt */
  purpose: string
  createHint: string
  deactivateHint: string
}

function defineReferenceContracts<
  const E extends string,
  Row extends z.ZodType,
  C extends z.ZodRawShape,
  U extends z.ZodRawShape,
  const K extends string,
  F extends z.ZodRawShape = Record<never, never>,
>(spec: ReferenceSpec<E, Row, C, U, F, K>) {
  const prefix = `masterdata.${spec.entity}` as const
  const keywords = [...spec.keywords]
  return {
    list: defineContract({
      name: `${prefix}.list`,
      title: `${spec.many} anzeigen`,
      description: `Listet die ${spec.many} des Betriebs.`,
      llm: { description: `Listet ${spec.many}. ${spec.purpose}`, keywords },
      input: listInput({
        sortBy: spec.sortBy,
        filters: { active: z.boolean().optional(), ...(spec.filters ?? ({} as F)) },
      }),
      output: listOutput(spec.row),
      permission: PERMISSION_AUTHENTICATED,
      risk: 'read',
    }),
    get: defineContract({
      name: `${prefix}.get`,
      title: `${spec.one} öffnen`,
      description: `Zeigt ${spec.accusative}.`,
      llm: { description: `Liefert ${spec.accusative} zu einer ID.`, keywords },
      input: zById,
      output: spec.row,
      permission: PERMISSION_AUTHENTICATED,
      risk: 'read',
    }),
    create: defineContract({
      name: `${prefix}.create`,
      title: `${spec.one} anlegen`,
      description: `Legt ${spec.accusative} an.`,
      llm: { description: `Legt ${spec.accusative} an. ${spec.createHint}`, keywords: [...keywords, 'anlegen', 'neu'] },
      input: z.strictObject(spec.create),
      output: zSaved,
      permission: spec.write,
      risk: 'write',
    }),
    update: defineContract({
      name: `${prefix}.update`,
      title: `${spec.one} ändern`,
      description: `Ändert ${spec.accusative}. Nur die genannten Felder ändern sich.`,
      llm: {
        description: `Ändert ${spec.accusative} – nur die mitgegebenen Felder; null leert ein freiwilliges Feld.`,
        keywords: [...keywords, 'ändern', 'umbenennen'],
      },
      input: z.strictObject({ id: zId, expectedVersion: zExpectedVersion, ...spec.update }),
      output: zSaved,
      permission: spec.write,
      risk: 'write',
    }),
    deactivate: defineContract({
      name: `${prefix}.deactivate`,
      title: `${spec.one} stilllegen`,
      description: `Nimmt ${spec.accusative} aus den Auswahllisten. Vorhandene Datensätze behalten den Verweis.`,
      llm: {
        description: `Legt ${spec.accusative} still – gelöscht wird nichts. ${spec.deactivateHint}`,
        keywords: [...keywords, 'stilllegen', 'deaktivieren', 'löschen'],
      },
      input: zToggle,
      output: zSaved,
      permission: spec.write,
      risk: 'destructive',
      effects: { reversible: true },
    }),
    reactivate: defineContract({
      name: `${prefix}.reactivate`,
      title: `${spec.one} wieder nutzen`,
      description: `Nimmt ${spec.accusative} wieder in die Auswahllisten auf.`,
      llm: { description: `Gegenstück zu ${prefix}.deactivate.`, keywords: [...keywords, 'reaktivieren'] },
      input: zToggle,
      output: zSaved,
      permission: spec.write,
      risk: 'write',
      effects: { reversible: true },
    }),
  }
}

const zKey = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/, { error: 'Bitte Kleinbuchstaben, Ziffern und Unterstrich verwenden, beginnend mit einem Buchstaben.' })

// ---- Einheiten ------------------------------------------------------------------------------------------------

export const zUnitRow = z.object({
  id: zId,
  version: z.int(),
  code: z.string(),
  name: z.string(),
  plural: z.string().nullable(),
  short: z.string(),
  kind: z.enum(UNIT_KINDS),
  decimals: z.int(),
  uneceCode: z.string().nullable(),
  active: z.boolean(),
  /** Artikel, die die Einheit als Basiseinheit oder Gebinde verwenden */
  usedBy: z.int(),
})

export const unitContracts = defineReferenceContracts({
  entity: 'unit',
  one: 'Einheit',
  many: 'Einheiten',
  accusative: 'eine Einheit',
  write: 'masterdata.unit.write',
  row: zUnitRow,
  sortBy: ['name', 'code'],
  filters: { kind: z.enum(UNIT_KINDS).optional() },
  keywords: ['Einheit', 'Maßeinheit', 'Gebindeart', 'Palette', 'Stück', 'Liter'],
  purpose:
    'Einheiten sind der Katalog, aus dem Artikel ihre Basiseinheit und ihre Gebinde wählen (Stück, Karton, Palette, ' +
    'kg, l). Die Umrechnung steht nicht hier, sondern am Artikel (masterdata.item.setUnits).',
  createHint:
    'Der Code ist ein fester Schlüssel (z. B. tray). `decimals` 0 = Stückware; 1–3 für Gewicht und Volumen. ' +
    'Der UN/ECE-Code (H87 = Stück, LTR = Liter) erscheint in der E-Rechnung.',
  deactivateHint: 'Eine Einheit, die aktive Artikel verwenden, lässt sich nicht stilllegen.',
  create: {
    code: zKey,
    name: zText(60),
    plural: zOptionalText(60),
    /** Kurzform für Tabellen und Belege: Fl, Ka, Pal */
    short: zText(8),
    kind: z.enum(UNIT_KINDS),
    decimals: z.int().min(0).max(3).default(0),
    uneceCode: zOptionalText(3),
  },
  update: {
    name: zText(60).optional(),
    plural: zOptionalText(60),
    short: zText(8).optional(),
    kind: z.enum(UNIT_KINDS).optional(),
    decimals: z.int().min(0).max(3).optional(),
    uneceCode: zOptionalText(3),
  },
})

export const unitList = unitContracts.list
export const unitGet = unitContracts.get
export const unitCreate = unitContracts.create
export const unitUpdate = unitContracts.update
export const unitDeactivate = unitContracts.deactivate
export const unitReactivate = unitContracts.reactivate

// ---- Steuerschlüssel und Steuersätze --------------------------------------------------------------------------

export const zTaxRate = z.object({ id: zId, validFrom: z.string(), rateBp: z.int(), effective: z.boolean() })

export const zTaxCodeRow = z.object({
  id: zId,
  version: z.int(),
  key: z.string(),
  name: z.string(),
  category: z.enum(TAX_CATEGORIES),
  exemptionReason: z.string().nullable(),
  /** Befreiungsgrund als VATEX-Code (BT-121) */
  exemptionCode: z.enum(VATEX_CODES).nullable(),
  /** Ersatzschlüssel für Kunden mit Steuerfall (AP2c): je Kategorie AE, K, G höchstens einer */
  taxCaseDefault: z.boolean(),
  note: z.string().nullable(),
  datevAccount: z.string().nullable(),
  datevTaxKey: z.string().nullable(),
  active: z.boolean(),
  /** Satz heute in Basispunkten; null = für heute ist kein Satz hinterlegt */
  rateBp: z.int().nullable(),
  /** alle Sätze, jüngster zuerst */
  rates: z.array(zTaxRate),
  usedBy: z.int(),
})

const taxFields = {
  name: zText(80),
  /** Umsatzsteuer-Kategorie der E-Rechnung (EN 16931): S Regelsatz, Z Nullsatz, E befreit, AE Reverse Charge … */
  category: z.enum(TAX_CATEGORIES),
  exemptionReason: zOptionalText(200),
  /**
   * Befreiungsgrund als VATEX-Code für die E-Rechnung (BT-121): VATEX-EU-AE nur bei AE, -IC nur bei K, -G nur bei G,
   * -O nur bei O, alle übrigen nur bei E; S und Z tragen keinen
   */
  exemptionCode: z.enum(VATEX_CODES).nullish(),
  /**
   * Ersatzschlüssel für den Steuerfall des Kunden (AP2c): Belegzeilen an EU-Unternehmen (K), Kunden im Drittland (G)
   * bzw. nach § 13b (AE) tragen statt 19 %/7 % diesen Schlüssel. Nur bei AE, K, G; nötig, wenn es mehrere gibt.
   */
  taxCaseDefault: z.boolean().optional(),
  /** Hinweistext auf dem Beleg („Steuerfrei nach § 4 UStG“) */
  note: zOptionalText(200),
  datevAccount: zOptionalText(9),
  datevTaxKey: zOptionalText(4),
}

export const taxCodeContracts = defineReferenceContracts({
  entity: 'taxCode',
  one: 'Steuerschlüssel',
  many: 'Steuerschlüssel',
  accusative: 'einen Steuerschlüssel',
  write: 'masterdata.tax.write',
  row: zTaxCodeRow,
  sortBy: ['name', 'key'],
  keywords: ['Steuer', 'Mehrwertsteuer', 'Umsatzsteuer', 'Steuersatz', 'MwSt', '19 %', '7 %', 'steuerfrei'],
  purpose:
    'Jeder Artikel trägt einen Steuerschlüssel; der Satz dazu ist datiert (maßgeblich ist das Leistungsdatum). ' +
    'Die Ausgabe nennt den heutigen Satz und alle Sätze mit Gültigkeitsbeginn.',
  createHint:
    'Mit `rateBp` (1900 = 19 %) und `validFrom` entsteht der erste Satz. Befreite und nicht steuerbare Kategorien ' +
    'haben 0 %; die Befreiung braucht einen Grund. Weitere Sätze: masterdata.taxRate.add.',
  deactivateHint: 'Ein Steuerschlüssel, den aktive Artikel tragen, lässt sich nicht stilllegen.',
  create: {
    key: zKey,
    ...taxFields,
    category: taxFields.category.default('S'),
    /** erster Steuersatz in Basispunkten */
    rateBp: zBasisPoints,
    /** Gültigkeitsbeginn des ersten Satzes, Standard heute */
    validFrom: zDay.optional(),
  },
  update: {
    name: taxFields.name.optional(),
    category: taxFields.category.optional(),
    exemptionReason: taxFields.exemptionReason,
    exemptionCode: taxFields.exemptionCode,
    taxCaseDefault: taxFields.taxCaseDefault,
    note: taxFields.note,
    datevAccount: taxFields.datevAccount,
    datevTaxKey: taxFields.datevTaxKey,
  },
})

export const taxCodeList = taxCodeContracts.list
export const taxCodeGet = taxCodeContracts.get
export const taxCodeCreate = taxCodeContracts.create
export const taxCodeUpdate = taxCodeContracts.update
export const taxCodeDeactivate = taxCodeContracts.deactivate
export const taxCodeReactivate = taxCodeContracts.reactivate

export const taxRateAdd = defineContract({
  name: 'masterdata.taxRate.add',
  title: 'Steuersatz ab Datum setzen',
  description:
    'Legt für einen Steuerschlüssel einen neuen Satz ab einem Datum an. Frühere Sätze bleiben – Belege mit älterem ' +
    'Leistungsdatum rechnen weiter mit ihrem Satz.',
  llm: {
    description:
      'Setzt einen neuen Steuersatz ab einem Stichtag (gesetzliche Änderung). Ein bereits gültiger Satz wird nie ' +
      'überschrieben; der neue gilt frühestens ab heute. `rateBp` in Basispunkten (700 = 7 %).',
    keywords: ['Steuersatz ändern', 'Mehrwertsteuer ändern', 'Steuersenkung', 'Steuererhöhung'],
  },
  input: z.strictObject({ taxCodeId: zId, validFrom: zDay, rateBp: zBasisPoints }),
  output: z.object({ id: zId }),
  permission: 'masterdata.tax.write',
  risk: 'financial',
})

export const taxRateRemove = defineContract({
  name: 'masterdata.taxRate.remove',
  title: 'Künftigen Steuersatz entfernen',
  description: 'Entfernt einen Steuersatz, der noch nicht gilt. Bereits gültige Sätze sind unveränderlich.',
  llm: {
    description: 'Nimmt einen vorgemerkten, noch nicht gültigen Steuersatz zurück.',
    keywords: ['Steuersatz zurücknehmen', 'Steuersatz löschen'],
  },
  input: zById,
  output: z.object({ taxCodeId: zId }),
  permission: 'masterdata.tax.write',
  risk: 'destructive',
})

// ---- Warengruppen (Baum) ----------------------------------------------------------------------------------------

export const zItemGroupRow = z.object({
  id: zId,
  version: z.int(),
  parentId: zId.nullable(),
  code: z.string().nullable(),
  name: z.string(),
  /** Namen von der Wurzel bis zur Gruppe: ['Bier', 'Helles'] */
  path: z.array(z.string()),
  depth: z.int(),
  defaultTaxCodeId: zId.nullable(),
  sort: z.int(),
  active: z.boolean(),
  /** Artikel direkt in dieser Gruppe */
  itemCount: z.int(),
  childCount: z.int(),
})

const groupFields = {
  name: zText(80),
  /** übergeordnete Gruppe; null = oberste Ebene */
  parentId: zOptionalId,
  code: zOptionalText(20),
  /** Steuerschlüssel, den neue Artikel der Gruppe vorgeschlagen bekommen */
  defaultTaxCodeId: zOptionalId,
  sort: z.int().min(0).max(100_000),
}

export const itemGroupContracts = defineReferenceContracts({
  entity: 'itemGroup',
  one: 'Warengruppe',
  many: 'Warengruppen',
  accusative: 'eine Warengruppe',
  write: 'masterdata.item.groups',
  row: zItemGroupRow,
  sortBy: ['sort', 'name'],
  filters: { parentId: zId.nullable().optional() },
  keywords: ['Warengruppe', 'Sortiment', 'Kategorie', 'Untergruppe'],
  purpose:
    'Warengruppen gliedern das Sortiment als Baum (Hauptgruppe › Untergruppe). Die Liste ist in Baumreihenfolge; `path` nennt die ' +
    'Namen von der Wurzel an.',
  createHint: '`parentId` hängt die Gruppe unter eine andere; ohne Angabe steht sie auf der obersten Ebene.',
  deactivateHint: 'Eine Gruppe mit aktiven Artikeln oder aktiven Untergruppen lässt sich nicht stilllegen.',
  create: { ...groupFields, sort: groupFields.sort.optional() },
  update: { ...groupFields, name: groupFields.name.optional(), sort: groupFields.sort.optional() },
})

export const itemGroupList = itemGroupContracts.list
export const itemGroupGet = itemGroupContracts.get
export const itemGroupCreate = itemGroupContracts.create
export const itemGroupUpdate = itemGroupContracts.update
export const itemGroupDeactivate = itemGroupContracts.deactivate
export const itemGroupReactivate = itemGroupContracts.reactivate

// ---- Zahlungsbedingungen ----------------------------------------------------------------------------------------

export const zPaymentTermRow = z.object({
  id: zId,
  version: z.int(),
  key: z.string(),
  name: z.string(),
  dueDays: z.int(),
  discountDays: z.int().nullable(),
  discountBp: z.int(),
  isDefault: z.boolean(),
  active: z.boolean(),
  usedBy: z.int(),
})

const zDueDays = z.int({ error: 'Bitte das Zahlungsziel in Tagen eingeben.' }).min(0).max(365)
const termFields = {
  name: zText(80),
  /** Zahlungsziel in Tagen ab Rechnungsdatum */
  dueDays: zDueDays,
  /** Skontofrist in Tagen; null = kein Skonto */
  discountDays: z.int().min(0).max(365).nullish(),
  /** Skonto in Basispunkten: 200 = 2 % */
  discountBp: zBasisPoints,
  /** Vorschlag für neue Kunden und Lieferanten (es gibt genau einen) */
  isDefault: z.literal(true),
}

export const paymentTermContracts = defineReferenceContracts({
  entity: 'paymentTerm',
  one: 'Zahlungsbedingung',
  many: 'Zahlungsbedingungen',
  accusative: 'eine Zahlungsbedingung',
  write: 'masterdata.paymentTerm.write',
  row: zPaymentTermRow,
  sortBy: ['name', 'dueDays'],
  keywords: ['Zahlungsbedingung', 'Zahlungsziel', 'Skonto', 'netto', 'Vorkasse', 'Lastschrift'],
  purpose: 'Zahlungsbedingungen bestimmen Fälligkeit und Skonto der Belege eines Kunden oder Lieferanten.',
  createHint: 'Skonto braucht eine Skontofrist, die nicht länger ist als das Zahlungsziel.',
  deactivateHint: 'Die Standard-Zahlungsbedingung lässt sich nicht stilllegen.',
  create: { key: zKey, ...termFields, discountBp: termFields.discountBp.default(0), isDefault: termFields.isDefault.optional() },
  update: {
    name: termFields.name.optional(),
    dueDays: termFields.dueDays.optional(),
    discountDays: termFields.discountDays,
    discountBp: termFields.discountBp.optional(),
    isDefault: termFields.isDefault.optional(),
  },
})

export const paymentTermList = paymentTermContracts.list
export const paymentTermGet = paymentTermContracts.get
export const paymentTermCreate = paymentTermContracts.create
export const paymentTermUpdate = paymentTermContracts.update
export const paymentTermDeactivate = paymentTermContracts.deactivate
export const paymentTermReactivate = paymentTermContracts.reactivate

// ---- Gründe -----------------------------------------------------------------------------------------------------

export const zReasonCodeRow = z.object({
  id: zId,
  version: z.int(),
  key: z.string(),
  name: z.string(),
  kind: z.enum(REASON_KINDS),
  requiresNote: z.boolean(),
  /** vom System verwendet (Inventurdifferenz, Anfangsbestand): nur der Name ist änderbar */
  isSystem: z.boolean(),
  sort: z.int(),
  active: z.boolean(),
})

export const reasonCodeContracts = defineReferenceContracts({
  entity: 'reasonCode',
  one: 'Grund',
  many: 'Gründe',
  accusative: 'einen Grund',
  write: 'masterdata.reasonCode.write',
  row: zReasonCodeRow,
  sortBy: ['sort', 'name'],
  filters: { kind: z.enum(REASON_KINDS).optional() },
  keywords: ['Grund', 'Bruch', 'Schwund', 'Verderb', 'Eigenverbrauch', 'Rückgabegrund', 'Rabattgrund', 'Stornogrund'],
  purpose:
    'Gründe begründen Bestandskorrekturen (Bruch, Schwund, Eigenverbrauch), Rückgaben, Rabatte, Stornos, Sperren und ' +
    'Kassenbewegungen. `kind` sagt, wofür ein Grund gilt.',
  createHint: '`requiresNote: true` verlangt bei jeder Verwendung eine Bemerkung.',
  deactivateHint: 'Gründe, die das System selbst verwendet, lassen sich nicht stilllegen.',
  create: {
    key: zKey,
    name: zText(80),
    kind: z.enum(REASON_KINDS),
    requiresNote: z.boolean().default(false),
    sort: z.int().min(0).max(100_000).optional(),
  },
  update: {
    name: zText(80).optional(),
    requiresNote: z.boolean().optional(),
    sort: z.int().min(0).max(100_000).optional(),
  },
})

export const reasonCodeList = reasonCodeContracts.list
export const reasonCodeGet = reasonCodeContracts.get
export const reasonCodeCreate = reasonCodeContracts.create
export const reasonCodeUpdate = reasonCodeContracts.update
export const reasonCodeDeactivate = reasonCodeContracts.deactivate
export const reasonCodeReactivate = reasonCodeContracts.reactivate

// ---- Auswahllisten für Formulare (ein Aufruf) -------------------------------------------------------------------

const zOption = z.object({ id: zId, name: z.string() })

/**
 * Alles, was Formulare und Belege an Auswahllisten brauchen, in EINEM Aufruf – nur aktive Einträge. Preisgruppen und
 * Pfandarten gehören anderen Modulen; hier stehen sie nur zum Auswählen.
 */
export const referenceOptions = defineContract({
  name: 'masterdata.reference.options',
  title: 'Auswahllisten laden',
  description: 'Aktive Einheiten, Steuerschlüssel, Warengruppen, Zahlungsbedingungen, Gründe und Preisgruppen.',
  llm: {
    description:
      'Liefert in einem Aufruf die Auswahllisten für Stammdaten und Belege: Einheiten, Steuerschlüssel mit heutigem ' +
      'Satz, Warengruppen (mit Pfad), Zahlungsbedingungen, Gründe und Preisgruppen – jeweils ID und Name. ' +
      'Verwenden, um aus einem Namen („Karton“, „19 %“, „Werkzeug“) die ID für eine andere Aktion zu ermitteln.',
    keywords: ['Auswahl', 'Einheit', 'Steuerschlüssel', 'Warengruppe', 'Zahlungsbedingung', 'Preisgruppe', 'Grund'],
  },
  input: z.strictObject({}),
  output: z.object({
    units: z.array(zOption.extend({ code: z.string(), short: z.string(), kind: z.enum(UNIT_KINDS), decimals: z.int() })),
    taxCodes: z.array(zOption.extend({ key: z.string(), rateBp: z.int().nullable(), category: z.string() })),
    itemGroups: z.array(zOption.extend({ parentId: zId.nullable(), path: z.array(z.string()), defaultTaxCodeId: zId.nullable() })),
    paymentTerms: z.array(zOption.extend({ key: z.string(), dueDays: z.int(), isDefault: z.boolean() })),
    reasonCodes: z.array(zOption.extend({ key: z.string(), kind: z.enum(REASON_KINDS), requiresNote: z.boolean() })),
    priceGroups: z.array(zOption.extend({ key: z.string(), priceMode: z.enum(['gross', 'net']), isDefault: z.boolean() })),
    depositTypes: z.array(
      zOption.extend({
        key: z.string(),
        shortName: z.string().nullable(),
        kind: z.string(),
        isComposite: z.boolean(),
        /** Pfandbetrag heute; null = kein Betrag hinterlegt */
        amountCents: z.int().nullable(),
        basis: z.enum(['gross', 'net']),
      }),
    ),
  }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
})
